import {
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  Input,
  Output,
  signal,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { WidgetDataStore } from '../../../../stores/widget-data.store';
import { RadioButton, RadioFieldComponent } from '../../../form';
import {
  InfoDetailItem,
  InfoDetailSection,
  InfoDetailWidget,
} from '../../models';

@Component({
  selector: 'siat-info-detail-widget',
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    RadioFieldComponent,
    MatButton,
    MatTooltipModule,
  ],
  templateUrl: './info-detail-widget.component.html',
  styleUrl: './info-detail-widget.component.scss',
})
export class InfoDetailWidgetComponent {
  @Input() widget!: InfoDetailWidget;
  @Input() modulo!: string;
  @Input() isApicActive = false;
  @Input() lockedPatient = false;
  @Output() saveConsensi = new EventEmitter<{ fse: boolean; dse: boolean }>();

  private widgetDataStore = inject(WidgetDataStore);
  /**
   * The form builder.
   */
  readonly formBuilder = inject(FormBuilder);

  /**
   * The formgroup.
   */
  consensiForm: FormGroup;
  /**
   * options for the radio component.
   */
  consensiOptItems: RadioButton[] = [
    { id: true, name: 'Si' },
    { id: false, name: 'No' },
  ];
  dseDisabled = signal<boolean>(false);

  rawData = computed<Record<string, unknown> | null>(() => {
    const data = this.widgetDataStore.getWidgetData<
      Record<string, unknown> | Record<string, unknown>[]
    >(this.widget?.id);
    if (!data) return null;
    return Array.isArray(data) ? (data[0] ?? null) : data;
  });

  get sections(): InfoDetailSection[] {
    return this.widget?.config?.sections || [];
  }

  constructor() {
    effect(() => {
      if (this.rawData()) {
        this.buildForm();
      }
    });
  }

  getFieldValue(field: string): unknown {
    const data = this.rawData();
    if (!data) return null;
    return field.split('.').reduce((obj: unknown, key: string) => {
      const record = obj as Record<string, unknown> | null | undefined;
      return record?.['consensiAsuit']
        ? (record['consensiAsuit'] as Record<string, unknown>)?.[key]
        : record?.[key];
    }, data as unknown);
  }

  shouldShowItem(item: InfoDetailItem): boolean {
    if (!item.showIf) return true;
    const val = this.getFieldValue(item.showIf);
    return val === 1 || val === true;
  }

  formatValue(
    value: string | number | boolean | Date | null | undefined,
    format?: string
  ): string {
    if (value === null || value === undefined) return '—';
    switch (format) {
      case 'boolean':
        return value === 1 || value === true ? 'Sì' : 'No';
      case 'date':
        return value
          ? new Date(value as string | number | Date).toLocaleDateString(
              'it-IT'
            )
          : '—';
      default:
        return String(value);
    }
  }

  fseControlChange() {
    const control = this.consensiForm.get('dse');
    const fseControl = this.consensiForm.get('fse');
    if (fseControl.value === true) {
      control.disable();
    }
    this.consensiForm.get('fse')?.valueChanges.subscribe(value => {
      if (value === true || value === true) {
        control.setValue(true);
        control.disable();
      } else {
        control.enable();
      }
    });
  }

  /**
   * Build the form.
   */
  buildForm(): void {
    const consensi = this.rawData();
    const consensiSiat = consensi?.['consensiSiat'] as
      | Record<string, unknown>
      | undefined;
    this.consensiForm = this.formBuilder.group({
      fse: [consensiSiat ? consensiSiat['fse'] : null],
      dse: [consensiSiat ? consensiSiat['dse'] : null],
      maggiorTutela: [consensiSiat ? consensiSiat['maggiorTutela'] : null],
    });
    this.fseControlChange();
    this.updateFormState();
  }

  private updateFormState(): void {
    if (!this.consensiForm) return;
    if (this.lockedPatient || this.isApicActive) {
      this.consensiForm.disable();
    } else {
      this.consensiForm.enable();
      this.fseControlChange();
    }
  }
  /**
   * Opens a confirmation modal before applying structure change.
   */
  confirmSave(): void {
    const confirmData = {
      fse: this.consensiForm.get('fse').value,
      dse: this.consensiForm.get('dse').value,
      maggiorTutela: this.consensiForm.get('maggiorTutela').value,
      hasConsensiSiat: this.rawData()['consensiSiat'],
    };
    this.saveConsensi.emit(confirmData);
  }
}
